import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginForm from '../components/auth/LoginForm';
import RegisterForm from '../components/auth/RegisterForm';
import ForgotPasswordForm from '../components/auth/ForgotPasswordForm';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Play, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { rememberPortal } from '../utils/customDomain';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgot, setIsForgot] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  // Redirect to dashboard if user is already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const handleAuthSuccess = (user) => {
    rememberPortal(null);
    navigate('/admin');
  };

  const handleDemoMode = () => {
    setIsDemoMode(true);
    // In demo mode, we'll use fallback data without authentication
    navigate('/admin?demo=true');
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render auth page if user is authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  if (isDemoMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Play className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Demo Mode</h2>
            <p className="text-gray-600 mb-6">
              You're now in demo mode. All data is simulated and no authentication is required.
            </p>
            <Button onClick={() => navigate('/admin')} className="w-full">
              Continue to Admin Panel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Demo Mode Option */}
        {/* <Card className="mb-6">
          <CardContent className="p-6 text-center">
            <Play className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-900 mb-2">Try Demo Mode</h3>
            <p className="text-sm text-gray-600 mb-4">
              Experience the app with sample data - no signup required
            </p>
            <Button 
              variant="outline" 
              onClick={handleDemoMode}
              className="w-full"
            >
              <Play className="w-4 h-4 mr-2" />
              Try Demo
            </Button>
          </CardContent>
        </Card> */}

        {/* Authentication Forms */}
        <div className="space-y-4">
          {isForgot ? (
            <ForgotPasswordForm onBackToLogin={() => setIsForgot(false)} />
          ) : isLogin ? (
            <LoginForm 
              onSuccess={handleAuthSuccess}
              onSwitchToRegister={() => setIsLogin(false)}
              onForgotPassword={() => setIsForgot(true)}
            />
          ) : (
            <RegisterForm 
              onSuccess={handleAuthSuccess}
              onSwitchToLogin={() => setIsLogin(true)}
            />
          )}
        </div>

        {/* Security Notice */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-900 text-sm">Secure Authentication</h4>
                <p className="text-xs text-gray-600 mt-1">
                  Your data is protected with industry-standard security measures.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
